import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { fetchMe, setToken, getToken, logoutUser, flushPartnerAttribution } from "../lib/api";
import { identifyUser, resetAnalytics, track } from "../lib/analytics";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);   // null = checking, false = anon, object = user
  const [authOpen, _setAuthOpen] = useState(false);
  const [authInitialMode, setAuthInitialMode] = useState("login");

  // Callers can either pass a bool (legacy) or an object { open, mode }
  const setAuthOpen = useCallback((next) => {
    if (typeof next === "object" && next !== null) {
      if (next.mode) setAuthInitialMode(next.mode);
      _setAuthOpen(!!next.open);
    } else {
      _setAuthOpen(!!next);
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!getToken()) { setUser(false); return; }
    try {
      const u = await fetchMe();
      setUser(u);
      identifyUser(u.user_id, { email: u.email, name: u.name });
      // Fire-and-forget: flush any stashed partner-link attribution to the
      // server. Silent — attribution rules live server-side.
      flushPartnerAttribution().catch(() => {});
    } catch { setToken(null); setUser(false); }
  }, []);

  useEffect(() => {
    // Skip /me while a Google callback session_id is being exchanged
    if (window.location.hash?.includes("session_id=")) { return; }
    refresh();
  }, [refresh]);

  const loginWithToken = (token, userObj, event) => {
    setToken(token);
    setUser(userObj);
    identifyUser(userObj?.user_id, { email: userObj?.email, name: userObj?.name });
    if (event) track(event);
    flushPartnerAttribution().catch(() => {});
  };
  const logout = async () => {
    try { await logoutUser(); } catch {}
    track("signed_out"); resetAnalytics();
    setToken(null); setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, refresh, loginWithToken, logout, authOpen, setAuthOpen, authInitialMode }}>
      {children}
    </AuthContext.Provider>
  );
};
